import { container } from 'inversify-props'
import { VendaServiceAdapter } from './adapter'
import { DetalhesDoPagamento, TurnoDeVenda, Venda } from '@/models'
import {
	imprimirNotaFiscal as imprimirNotaFiscalPadrao,
	obterTrocoDaVenda,
	unificarItensDaVendaPorProduto,
} from '@/shareds/venda-shareds'
import { VendaModule } from '@/store/vuex/venda/VendaStore'
import AlertModule from '@/store/vuex/aplicacao/AlertModule'
import PluginModule from '@/store/vuex/aplicacao/PluginStore'
import { imprimirCupomSemValorFiscal as imprimirCupomSemValorFiscalPadrao } from '@/shareds/pedidos/impressao-cupom-sem-valor-fiscal'
import { FindEstoqueUseCase } from '../deposito'

type UseCaseParams = {
	venda: Venda
	detalhesDosPagamentos: DetalhesDoPagamento[]
	turnoDeVenda: TurnoDeVenda
	service?: VendaServiceAdapter
	gerarCupom: boolean
	confirmacaoDeValeTroca?: { mostrar: () => Promise<boolean> }
	imprimirCupomSemValorFiscal?: (venda: Venda, pagamentos: DetalhesDoPagamento[]) => Promise<any>
	imprimirNotaFiscal?: (idVenda: string, imprimirNfe?: boolean, isContingencia?: boolean) => Promise<any>
	vendaModule?: typeof VendaModule
	findEstoqueUseCase?: FindEstoqueUseCase
}

export async function FinalizarVendaUseCase({
	service = container.get<VendaServiceAdapter>('VendaServiceAdapter'),
	imprimirNotaFiscal = imprimirNotaFiscalPadrao,
	imprimirCupomSemValorFiscal = imprimirCupomSemValorFiscalPadrao,
	vendaModule = VendaModule,
	venda,
	detalhesDosPagamentos,
	gerarCupom,
	turnoDeVenda,
	findEstoqueUseCase = new FindEstoqueUseCase(),
}: UseCaseParams): Promise<Venda> {
	if (!venda) throw new Error('Venda não informada')
	if (!turnoDeVenda) throw new Error('Turno de venda não informado')
	if (!venda.itens || venda.itens.length === 0) throw new Error('Venda não possui itens')

	let identificador = ''
	try {
		identificador = await service.gerarIdentificadorDeVenda(turnoDeVenda.pontoDeVenda.id)
	} catch (error) {
		throw Error('Falha ao finalizar a venda, tente novamente ou contate o suporte.')
	}

	const gerarEstoqueNaVenda = turnoDeVenda.pontoDeVenda.loja.configuracaoDaLoja?.podeGerarEstoqueNaVenda || false
	if ((!gerarEstoqueNaVenda || turnoDeVenda.pontoDeVenda.criaPedidoNaVenda) && venda.tipoDeTransacao === 'Venda') {
		const itensUnificados = unificarItensDaVendaPorProduto(venda.itens)
		let projecoesPorItemDaVenda = await Promise.all(
			itensUnificados.map(async (itemDaVenda: any) => {
				let criticas = ''
				await findEstoqueUseCase.getEstoqueDoItemDaVendaNaLoja(
					itemDaVenda.id, itemDaVenda.quantidade.toString(), turnoDeVenda?.pontoDeVenda.loja.id || '',
				).catch((error) => {
					criticas = error instanceof Object ? error.response.data[0] : error
				})
				return criticas
			}),
		)
		projecoesPorItemDaVenda = projecoesPorItemDaVenda.filter(value => value !== '')
		const criticasNormalizadas = projecoesPorItemDaVenda
			? projecoesPorItemDaVenda.join('<br>')
			: undefined
		if (criticasNormalizadas) {
			throw criticasNormalizadas
		}
	}

	const vendaAEnviar: Venda = {
		...venda,
		troco: obterTrocoDaVenda(venda),
		tipoDeCliente: venda.tipoDeCliente || null,
		pontoDeVenda: turnoDeVenda.pontoDeVenda,
		dataHora: venda.dataHora || new Date(),
		identificador: venda.identificador,
	}
	vendaModule.setVendaAtual(vendaAEnviar)

	const vendaAtualizada = await service.create(vendaAEnviar)

	// Nova venda
	if (!venda.identificador) vendaModule.adicionarVendaDoTurno(vendaAtualizada)

	if (gerarCupom && vendaAtualizada.pontoDeVenda?.tipoDeOperacao !== 'Orçamento') {
		try {
			await imprimirNotaFiscal(vendaAtualizada.id)
		} catch (error) {
			AlertModule.setError(error)
		}
	}

	PluginModule.emit("finalizarVenda", { venda: vendaAtualizada })

	if (vendaAtualizada.tipoDeTransacao === 'Devolução') {
		AlertModule.setSuccess("Devolução realizada com sucesso")
	} else if (vendaAtualizada.pontoDeVenda?.imprimeReciboNoOrcamento && !gerarCupom) {
		const vendaComCliente = {
			...vendaAtualizada,
			cliente: venda.cliente,
		}
		await imprimirCupomSemValorFiscal(vendaComCliente, detalhesDosPagamentos)
	}

	vendaModule.iniciarNovaVenda({ identificador })

	return vendaAtualizada
}